import * as React from "react"
import { Search, X } from "lucide-react"
import type { Table } from "@tanstack/react-table"

import { Input } from "../../../internal/primitives/input"
import { DataTableViewOptions } from "./DataTableViewOptions"
import { cn } from "../../../lib/utils"

export interface DataTableToolbarProps<TData> {
  table: Table<TData>
  searchColumn?: string
  searchMode?: "column" | "global"
  searchPlaceholder?: string
  searchLabel?: string
  searchDebounceMs?: number
  searchValue?: string
  onSearchChange?: (value: string) => void
  resultCount?: number
  /** Persist the full uncontrolled table query (q, facets, sort, and pagination) in the URL. */
  persistSearchInUrl?: boolean
  showSearch?: boolean
  filters?: React.ReactNode
  bulkActions?: React.ReactNode
  className?: string
}

function formatFilterValue(value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "None"
    }

    return value.length === 1 ? String(value[0]) : `${value.length} selected`
  }

  if (value === null || value === undefined || value === "") {
    return "Set"
  }

  return String(value)
}

export function DataTableToolbar<TData>({
  table,
  searchColumn,
  searchMode,
  searchPlaceholder = "Search...",
  searchLabel = "Search table",
  searchDebounceMs = 250,
  searchValue,
  onSearchChange,
  resultCount,
  persistSearchInUrl = false,
  showSearch = true,
  filters,
  bulkActions,
  className,
}: DataTableToolbarProps<TData>) {
  const resolvedSearchMode = searchMode ?? (searchColumn ? "column" : "global")
  const isControlledSearch = searchValue !== undefined
  const shouldPersistSearchInUrl = persistSearchInUrl && !isControlledSearch
  const tableSearchValue = resolvedSearchMode === "column"
      ? ((searchColumn ? table.getColumn(searchColumn)?.getFilterValue() : "") as string | undefined) ?? ""
      : ((table.getState().globalFilter as string | undefined) ?? "")
  const [urlSearchValue, setUrlSearchValue] = React.useState(() => {
    if (!shouldPersistSearchInUrl || typeof window === "undefined") {
      return ""
    }
    return new URLSearchParams(window.location.search).get("q") ?? ""
  })
  const currentSearchValue = isControlledSearch
    ? searchValue ?? ""
    : shouldPersistSearchInUrl
      ? urlSearchValue
      : tableSearchValue
  const [draftSearchValue, setDraftSearchValue] = React.useState(currentSearchValue)
  const [urlStateReady, setUrlStateReady] = React.useState(!shouldPersistSearchInUrl)

  const reservedQueryKeys = React.useMemo(
    () => new Set(["q", "sort", "page", "page_size", "registry"]),
    []
  )

  const applyTableSearch = React.useCallback((value: string) => {
    if (resolvedSearchMode === "column" && searchColumn) {
      table.getColumn(searchColumn)?.setFilterValue(value || undefined)
      return
    }
    table.setGlobalFilter(value || undefined)
  }, [resolvedSearchMode, searchColumn, table])

  const replaceUrlTableState = React.useCallback((value: string) => {
    if (!shouldPersistSearchInUrl || typeof window === "undefined") {
      return
    }
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set("q", value)
    } else {
      url.searchParams.delete("q")
    }
    for (const column of table.getAllColumns()) {
      if (reservedQueryKeys.has(column.id)) {
        continue
      }
      url.searchParams.delete(column.id)
      const filterValue = column.getFilterValue()
      const values = Array.isArray(filterValue)
        ? filterValue
        : filterValue === null || filterValue === undefined || filterValue === ""
          ? []
          : [filterValue]
      for (const facetValue of [...new Set(values.map(String).map((item) => item.trim()).filter(Boolean))].sort()) {
        url.searchParams.append(column.id, facetValue)
      }
    }
    url.searchParams.delete("sort")
    for (const sort of table.getState().sorting) {
      url.searchParams.append("sort", sort.desc ? `-${sort.id}` : sort.id)
    }
    const { pageIndex, pageSize } = table.getState().pagination
    if (pageIndex > 0) {
      url.searchParams.set("page", String(pageIndex + 1))
    } else {
      url.searchParams.delete("page")
    }
    url.searchParams.set("page_size", String(pageSize))
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
    window.dispatchEvent(new PopStateEvent("popstate"))
    setUrlSearchValue(value)
  }, [reservedQueryKeys, shouldPersistSearchInUrl, table])

  React.useEffect(() => {
    setDraftSearchValue(currentSearchValue)
  }, [currentSearchValue])

  React.useEffect(() => {
    if (!shouldPersistSearchInUrl || typeof window === "undefined") {
      return
    }

    const restoreSearchFromUrl = () => {
      const nextValue = new URLSearchParams(window.location.search).get("q")?.trim() ?? ""
      const searchParams = new URLSearchParams(window.location.search)
      setUrlSearchValue(nextValue)
      setDraftSearchValue(nextValue)
      applyTableSearch(nextValue)
      for (const column of table.getAllColumns()) {
        if (reservedQueryKeys.has(column.id)) {
          continue
        }
        const values = searchParams.getAll(column.id)
        column.setFilterValue(values.length > 0 ? values : undefined)
      }
      table.setSorting(searchParams.getAll("sort").flatMap((token) => {
        const normalizedToken = token.trim()
        const desc = normalizedToken.startsWith("-")
        const id = desc ? normalizedToken.slice(1) : normalizedToken
        return id ? [{ id, desc }] : []
      }))
      const parsedPage = Number.parseInt(searchParams.get("page") ?? "1", 10)
      const parsedPageSize = Number.parseInt(
        searchParams.get("page_size") ?? String(table.getState().pagination.pageSize),
        10
      )
      table.setPagination({
        pageIndex: Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0,
        pageSize: Number.isFinite(parsedPageSize) && parsedPageSize > 0
          ? parsedPageSize
          : table.getState().pagination.pageSize,
      })
      setUrlStateReady(true)
    }

    restoreSearchFromUrl()
    window.addEventListener("popstate", restoreSearchFromUrl)
    return () => window.removeEventListener("popstate", restoreSearchFromUrl)
  }, [applyTableSearch, reservedQueryKeys, shouldPersistSearchInUrl, table])

  const tableUrlStateSignature = JSON.stringify({
    columnFilters: table.getState().columnFilters,
    sorting: table.getState().sorting,
    pagination: table.getState().pagination,
  })

  React.useEffect(() => {
    if (!shouldPersistSearchInUrl || !urlStateReady) {
      return
    }
    replaceUrlTableState(currentSearchValue.trim())
  }, [
    currentSearchValue,
    replaceUrlTableState,
    shouldPersistSearchInUrl,
    tableUrlStateSignature,
    urlStateReady,
  ])

  React.useEffect(() => {
    if (draftSearchValue === currentSearchValue) {
      return
    }

    const commitSearch = () => {
      if (isControlledSearch) {
        onSearchChange?.(draftSearchValue.trim())
        return
      }
      const nextValue = draftSearchValue.trim()
      applyTableSearch(nextValue)
      replaceUrlTableState(nextValue)
    }

    if (searchDebounceMs <= 0) {
      commitSearch()
      return
    }

    const timeout = window.setTimeout(commitSearch, searchDebounceMs)
    return () => window.clearTimeout(timeout)
  }, [
    currentSearchValue,
    draftSearchValue,
    isControlledSearch,
    onSearchChange,
    applyTableSearch,
    replaceUrlTableState,
    searchDebounceMs,
  ])

  const handleClearSearch = () => {
    setDraftSearchValue("")
    if (isControlledSearch) {
      onSearchChange?.("")
      return
    }
    applyTableSearch("")
    replaceUrlTableState("")
  }

  const activeSearchValue = currentSearchValue.trim()
  const activeFilters = [
    ...((activeSearchValue
      ? [
          {
            key: "__global__",
            label: "Search",
            value: activeSearchValue,
            clear: handleClearSearch,
          },
        ]
      : []) as {
      key: string
      label: string
      value: string
      clear: () => void
    }[]),
    ...table.getState().columnFilters.map((filter) => ({
      key: filter.id,
      label: filter.id.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase()),
      value: formatFilterValue(filter.value),
      clear: () => table.getColumn(filter.id)?.setFilterValue(undefined),
    })),
  ]
  const isFiltered = activeFilters.length > 0

  const filteredRowCount = resultCount ?? table.getFilteredRowModel().rows.length

  const shouldRenderSearch = Boolean(
    showSearch && (
    (resolvedSearchMode === "column" && searchColumn) || resolvedSearchMode === "global"
    )
  )

  return (
    <div className={cn("border-b border-border/40 bg-muted/2 px-4 py-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {shouldRenderSearch && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                aria-label={searchLabel}
                placeholder={searchPlaceholder}
                maxLength={200}
                value={draftSearchValue}
                onChange={(event) => setDraftSearchValue(event.target.value)}
                className={cn(
                  "h-8 w-full bg-background/50 pl-8 text-xs sm:w-[150px] lg:w-[250px]",
                  draftSearchValue && "pr-8"
                )}
              />
              {draftSearchValue && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/40 transition-colors hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          {filters}
          {bulkActions}
        </div>
        <DataTableViewOptions table={table} />
      </div>
      {activeFilters.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground/80">
            {filteredRowCount} result{filteredRowCount !== 1 ? "s" : ""}
          </span>
          <span className="select-none text-border/60" aria-hidden>·</span>
          {activeFilters.map((filter) => (
            <span
              key={filter.key}
              className="inline-flex items-center gap-1 rounded-md border border-border/40 bg-muted/30 px-2 py-0.5 text-[11px] font-medium"
            >
              <span className="text-muted-foreground/70">{filter.label}:</span>
              <span className="text-foreground/90">{filter.value}</span>
              <button
                type="button"
                aria-label={`Clear ${filter.label} filter`}
                onClick={filter.clear}
                className="ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm text-muted-foreground/40 transition-colors hover:bg-muted/80 hover:text-foreground"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {isFiltered && (
            <button
              type="button"
              onClick={() => {
                table.resetColumnFilters()
                handleClearSearch()
              }}
              className="ml-1 inline-flex h-6 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-muted-foreground/80 transition-colors hover:bg-muted/80 hover:text-foreground"
            >
              <X className="h-2.5 w-2.5" />
              Reset all
            </button>
          )}
        </div>
      )}
    </div>
  )
}
